import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Card from '../elements/Card';
import Button from '../elements/Button';
import Icon from '../common/Icon';
import Loading from '../common/Loading';
import { apiRequest, apiFormRequest } from '../../utils';
import '../../pages/finance/Finance.css';

/* The invoice half of the internal home: a camera, and what is still unpaid.

   It sits on the SAME screen as the CRM rather than behind its own role, because
   the person holding a supplier invoice is very often the person who was just
   logging a visit, and making them change role to photograph a bill is exactly
   the friction the capture exists to remove. The operations screens will arrive
   the same way — another section here, drawn for whoever is granted it.

   Deliberately NOT asking which payment it belongs to: the invoice arrives weeks
   before the statement is imported, so pairing them here would mean holding the
   paper until the bank catches up. */
const InvoiceCapture = ({ buId }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [unpaid, setUnpaid] = useState([]);
  const [count, setCount] = useState(null);
  const [scanning, setScanning] = useState(false);
  const [error, setError] = useState(null);

  const cameraRef = useRef(null);
  const galleryRef = useRef(null);

  const load = useCallback(() => {
    if (!buId) return;
    apiRequest('GET', `/finance/documents?view=unpaid&bu_id=${buId}`)
      .then((d) => { setUnpaid(d.documents || []); setCount(d.total_count ?? null); })
      .catch(() => { setUnpaid([]); setCount(null); });
  }, [buId]);

  useEffect(() => { load(); }, [load]);

  const scan = async (event) => {
    const file = event.target.files?.[0];
    // Let the same invoice be photographed again after a bad read.
    event.target.value = '';
    if (!file || !buId) return;

    setScanning(true);
    setError(null);
    try {
      const form = new FormData();
      form.append('image', file);
      form.append('bu_id', buId);
      let draft = {};
      try {
        const { invoice } = await apiFormRequest('/finance/scan', form);
        draft = invoice || {};
      } catch {
        // A failed read is not a failed capture. The paper is in front of them
        // and the fields can be typed — losing the photo over a 502 would be the
        // one outcome worth avoiding.
        setError(t('financeScanFailed'));
      }
      // The photo travels to the confirm screen in the router's own state, not
      // through storage: nothing is stored until somebody presses save. A File is
      // structured-cloneable, which is what history state accepts.
      navigate('/finance/new', { state: { draft, photo: file, buId } });
    } finally {
      setScanning(false);
    }
  };

  return (
    <Card>
      <button type="button" className="fin-camera" disabled={scanning || !buId}
              onClick={() => cameraRef.current?.click()}>
        {scanning
          ? <Loading size="sm" showLabel={false} inline />
          : <Icon name="camera" />}
        <span>{scanning ? t('financeReading') : t('financePhotographInvoice')}</span>
      </button>
      <button type="button" className="fin-gallery" disabled={scanning || !buId}
              onClick={() => galleryRef.current?.click()}>
        <Icon name="picture" /> {t('financeFromGallery')}
      </button>
      <input type="file" accept="image/*" capture="environment" ref={cameraRef}
             className="d-none" onChange={scan} />
      <input type="file" accept="image/*" ref={galleryRef}
             className="d-none" onChange={scan} />
      {error && <p className="fin-error">{error}</p>}
      <p className="fin-hint">{t('financeCaptureHint')}</p>

      {!!unpaid.length && (
        <>
          <div className="fin-row fin-row--head mt-3">
            <h6>{t('financeWaitingToBePaid')}</h6>
            <span className="fin-count">{count ?? unpaid.length}</span>
          </div>
          {unpaid.slice(0, 5).map((d) => (
            <div key={d.id} className="fin-doc" onClick={() => navigate(`/finance/${d.id}`)}>
              <div className="fin-doc__main">
                <span className="fin-doc__name">
                  {d.supplier_name || t('financeUnknownSupplier')}
                </span>
                <span className="fin-doc__sub">
                  {[d.document_number, d.category_name].filter(Boolean).join(' · ')}
                </span>
              </div>
              <div className="fin-doc__side">
                {d.amount_gross != null && (
                  <span className="fin-doc__amount">
                    {d.amount_gross.toLocaleString('nl-BE', { minimumFractionDigits: 2 })}
                  </span>
                )}
                {!d.has_photo && <span className="fin-doc__flag">{t('financeNoPhoto')}</span>}
              </div>
              <Icon name="angle-right" />
            </div>
          ))}
        </>
      )}

      <Button theme="secondary" mb="0" onClick={() => navigate('/finance/list')}>
        {t('financeSeeAll')}
      </Button>
    </Card>
  );
};

export default InvoiceCapture;
